import { ReactNode, useState } from 'react';
import { Check, Copy, Eye, EyeOff } from 'lucide-react';
import { Button, Card, Typography } from '../ui';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import styles from './ConnectAppCard.module.css';

const CONNECTIONS_ROUTE = 'settings-connections';

export interface ConnectAppCardProps {
  /** The Lucide glyph shown in the card head — rendered by the caller (e.g. `<Bot size={20} />`). */
  icon: ReactNode;
  /** Product/brand name of the client. Verbatim — never localized. */
  title: string;
  /** The one-line "how to connect" instruction. */
  instructions: string;
  /** The literal value the user copies — an MCP server URL or an A2A Agent Card JSON. NOT localized. */
  value: string;
  /** Distinct BI `button_name` for the copy click, e.g. `copy-chatgpt-url`. */
  copyBiName: string;
  /**
   * Hide the value behind a "View" toggle instead of always showing it. For a long value (the A2A
   * Agent Card JSON) an always-open block dominates the screen; Copy is what the user actually needs,
   * so it stays visible and the block is opt-in.
   */
  collapsible?: boolean;
}

/**
 * A "connect an app" instruction card: a titled head with a short how-to, and a copyable literal
 * value (an MCP server URL or an A2A Agent Card JSON) with a Copy → Copied button.
 *
 * Mirrors ConnectClaudeCard's copy UX and BI, but for a STATIC value — there is no token to mint,
 * so nothing is fetched and the value is always on screen and selectable.
 */
export function ConnectAppCard({ icon, title, instructions, value, copyBiName, collapsible = false }: ConnectAppCardProps) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [copied, setCopied] = useState(false);
  // The value block is opt-in behind "View" (collapsible cards only, e.g. Gemini's Agent Card JSON).
  // The Claude/ChatGPT cards never show their URL — Copy still writes it to the clipboard (Figma).
  const [shown, setShown] = useState(false);

  const onCopy = async () => {
    logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: copyBiName });
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked (permissions, insecure context) — the block is on screen and selectable,
      // so the user can still copy by hand; nothing to recover.
    }
  };

  return (
    <Card variant="sunken" className={styles.card}>
      <div className={styles.head}>
        <span className={styles.icon} aria-hidden>
          {icon}
        </span>
        <Typography variant="h4">{title}</Typography>
      </div>
      <div className={styles.divider} aria-hidden />
      <Typography variant="p2" color="secondary" className={styles.instructions}>
        {instructions}
      </Typography>

      {shown ? <pre className={styles.skill}>{value}</pre> : null}
      <div className={styles.skillActions}>
        {/* View (eye) on the left, Copy on the right — Figma 13936-256725. View is a collapsible-only
            toggle (Gemini's Agent Card JSON); the URL cards show Copy alone. */}
        {collapsible ? (
          <Button
            variant="outlined"
            size="large"
            fullWidth
            startIcon={shown ? <EyeOff size={16} aria-hidden /> : <Eye size={16} aria-hidden />}
            onClick={() => {
              logPageClickBi({ page_name: CONNECTIONS_ROUTE, button_name: `${copyBiName}-toggle` });
              setShown((s) => !s);
            }}
          >
            {shown ? t('connections.connect.hide') : t('connections.connect.view')}
          </Button>
        ) : null}
        <Button
          variant="outlined"
          size="large"
          fullWidth
          startIcon={copied ? <Check size={16} aria-hidden /> : <Copy size={16} aria-hidden />}
          onClick={onCopy}
        >
          {copied ? t('connections.connect.copied') : t('connections.connect.copy')}
        </Button>
      </div>
    </Card>
  );
}
